import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { recordWeighIn, newRequestId } from '../services/weightService';
import { toastError, toastSuccess } from '../ui/toast';
import cipherLogo from '../assets/cipher-icon-only-transparent.png';
import WheelColumn from './ui/WheelColumn';
import CoachCodeInput from './CoachCodeInput';
import { colors as theme } from '../theme';

const HEIGHT_OPTIONS = Array.from({ length: 151 }, (_, i) => {
  const cm = 100 + i;
  return { value: cm, label: `${cm} cm` };
});

const c = {
  bg: theme.bg, card: theme.surface1, border: theme.surface3,
  text: theme.text, muted: theme.textMuted,
  gold: theme.gold, goldDark: theme.goldPressed,
  green: '#4A8F62', error: theme.danger,
};

const STEPS = [
  { id: 'age',        title: 'How old are you?',            type: 'number', unit: 'years', placeholder: '30', min: 13, max: 100 },
  { id: 'sex',        title: 'Biological sex?',             type: 'radio' },
  { id: 'height',     title: 'Your height?',                type: 'number', unit: 'cm',    placeholder: '170', min: 100, max: 250 },
  { id: 'weight',     title: 'Current weight?',             type: 'number', unit: 'kg',    placeholder: '75',  min: 30,  max: 300 },
  { id: 'location',   title: 'Where will you train?',       type: 'radio' },
  { id: 'frequency',  title: 'How often will you train?',   type: 'radio' },
  { id: 'experience', title: 'Experience level?',           type: 'radio' },
  { id: 'injuries',   title: 'Any injuries or conditions?', type: 'radio' },
  { id: 'goal',       title: 'Primary training goal?',      type: 'radio' },
  { id: 'coach_code', title: 'Have a coach code?',          type: 'coach_code' },
];

const OPTIONS = {
  sex: [
    { value: 'male',   label: 'Male' },
    { value: 'female', label: 'Female' },
  ],
  location: [
    { value: 'gym',  label: 'Gym' },
    { value: 'home', label: 'Home' },
  ],
  frequency: [
    { value: '2-3', label: '2–3 days/week' },
    { value: '3-4', label: '3–4 days/week' },
    { value: '4-5', label: '4–5 days/week' },
    { value: '5-6', label: '5–6 days/week' },
  ],
  experience: [
    { value: 'beginner',     label: 'Beginner',     sub: 'New to training' },
    { value: 'intermediate', label: 'Intermediate', sub: '6+ months experience' },
    { value: 'advanced',     label: 'Advanced',     sub: '2+ years experience' },
  ],
  injuries: [
    { value: 'no',  label: 'No injuries' },
    { value: 'yes', label: 'Yes, I have injuries' },
  ],
  goal: [
    { value: 'lose_weight',     label: 'Lose Weight' },
    { value: 'build_muscle',    label: 'Build Muscle' },
    { value: 'get_stronger',    label: 'Get Stronger' },
    { value: 'improve_fitness', label: 'Improve Fitness' },
    { value: 'maintain',        label: 'Maintain' },
  ],
};

export default function FitnessBridgeSetup({ user, onComplete, onLogout }) {
  const weighInRequestId = useRef(newRequestId()); // the starting weigh-in, once
  const [step, setStep]             = useState(0);
  const [answers, setAnswers]       = useState({ age: '', sex: '', height: '', weight: '', location: '', frequency: '', experience: '', injuries: '', goal: '', injuryDetails: '' });
  const [submitting, setSubmitting] = useState(false);
  const [nutritionName, setNutritionName] = useState('');
  const [validatedCoach, setValidatedCoach] = useState(null); // set by CoachCodeInput when a valid code is entered

  // Pre-load shared data from nutrition profile
  useEffect(() => {
    const fetchNutritionProfile = async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, height_cm, current_weight_kg, sex')
        .eq('id', user.id)
        .single();
      if (!profile) return;
      if (profile.first_name) setNutritionName(profile.first_name.split(' ')[0]);
      setAnswers(prev => ({
        ...prev,
        height: profile.height_cm         ? String(Math.round(profile.height_cm))          : prev.height,
        weight: profile.current_weight_kg  ? String(profile.current_weight_kg)  : prev.weight,
        sex:    profile.sex && ['male', 'female'].includes(profile.sex) ? profile.sex       : prev.sex,
      }));
    };
    fetchNutritionProfile();
  }, [user.id]);

  const current = STEPS[step];

  const canAdvance = (() => {
    if (current.type === 'coach_code') return true; // optional — never blocks, no `answers` entry to check
    const val = answers[current.id];
    if (!val && val !== 0) return false;
    if (current.type === 'number') {
      const num = parseFloat(val);
      return !isNaN(num) && num >= current.min && num <= current.max;
    }
    if (current.id === 'injuries') {
      return answers.injuries === 'no' || answers.injuryDetails.trim().length >= 5;
    }
    return true;
  })();

  const advance = () => {
    if (step < STEPS.length - 1) { setStep(s => s + 1); return; }
    submit();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const update = {
        age:               parseInt(answers.age)    || null,
        sex:               answers.sex              || null,
        height:            parseFloat(answers.height) || null,
        weight:            parseFloat(answers.weight) || null,
        bodyweight_kg:     parseFloat(answers.weight) || null,
        bodyweight_updated_at: new Date().toISOString(),
        training_location: answers.location,
        exercise_frequency: answers.frequency,
        experience_level:  answers.experience,
        primary_goal:      answers.goal,
        has_injuries:      answers.injuries === 'yes',
      };
      if (answers.injuries === 'yes' && answers.injuryDetails) update.injuries = answers.injuryDetails;
      // Only override coach_id when a real code was entered and validated — a
      // nutrition-only user upgrading via this bridge may already have a
      // fallback coach_id from their earlier eager profile row (see
      // create-checkout-session.js), so leave it untouched when this step is
      // skipped rather than re-deriving a fallback here too.
      if (validatedCoach?.auth_user_id) update.coach_id = validatedCoach.auth_user_id;

      // Only a set-up not yet finished is completed here, and a row must come back: a form left open in
      // one tab while the set-up was finished in another used to save its older answers over the new
      // ones (Codex's task 019, R3). Later changes to these answers belong to Settings, not here.
      const { data: finished, error } = await supabase
        .from('users')
        .update(update)
        .eq('auth_user_id', user.id)
        .is('training_location', null)
        .select('id');
      if (error) throw error;
      if (!finished?.length) {
        const { data: row, error: readError } = await supabase
          .from('users')
          .select('training_location')
          .eq('auth_user_id', user.id)
          .maybeSingle();
        if (readError) throw readError;
        if (!row?.training_location) throw new Error("Your answers weren't saved. Please try again.");
        // Finished already, elsewhere: nothing is changed, no second weigh-in or programme match
        toastSuccess('Your Fitness set-up was already finished, so nothing was changed.');
        onComplete();
        return;
      }

      // The weight is a weigh-in in the one weight record (migration 080); the database sets the
      // current weight in both apps. One request id per set-up, so a second press adds nothing.
      if (update.weight != null) {
        try {
          await recordWeighIn(user.id, update.weight, {
            notes: 'Starting weight from fitness bridge setup', requestId: weighInRequestId.current,
          });
        } catch (syncErr) {
          console.warn('[FitnessBridgeSetup] weight not added to the weight record (non-fatal):', syncErr);
        }
      }

      // Auto-assign best matching program (fire and forget)
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        fetch('/api/match-program', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({}),
        })
          .then(async res => {
            if (!res.ok) {
              const body = await res.text().catch(() => '(no body)');
              console.error(`[FitnessBridgeSetup] match-program failed — status ${res.status} — user ${user.id} — ${body}`);
            }
          })
          .catch(err => {
            console.error(`[FitnessBridgeSetup] match-program network error — user ${user.id} —`, err);
          });
      } else {
        console.error(`[FitnessBridgeSetup] match-program skipped — no active session for user ${user.id}`);
      }

      onComplete();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100dvh', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "'Inter', sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 480 }}>

        {/* Logo + heading */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ width: 64, height: 64, margin: '0 auto 10px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={cipherLogo} alt="Cipher" style={{ width: 56, height: 56 }} />
          </div>
          <div style={{ color: c.gold, fontSize: 14, fontWeight: 700, letterSpacing: 4, fontFamily: "'Orbitron', sans-serif" }}>CIPHER</div>
          {nutritionName && (
            <p style={{ color: c.muted, fontSize: 13, marginTop: 6 }}>
              Welcome, {nutritionName} — just a few details to get you started.
            </p>
          )}
        </div>

        {/* Progress */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ height: 4, background: c.card, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${((step + 1) / STEPS.length) * 100}%`, background: `linear-gradient(90deg, ${c.gold}, ${c.goldDark})`, transition: 'width 0.3s' }} />
          </div>
          <p style={{ color: c.muted, fontSize: 12, marginTop: 6, textAlign: 'center' }}>Step {step + 1} of {STEPS.length}</p>
        </div>

        {/* Card */}
        <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 20, padding: '28px 24px', marginBottom: 16 }}>
          <h2 style={{ color: c.text, fontSize: 20, fontWeight: 700, margin: '0 0 20px', fontFamily: "'Inter', sans-serif" }}>
            {current.title}
          </h2>

          {current.id === 'height' ? (
            <WheelColumn
              options={HEIGHT_OPTIONS}
              value={parseInt(answers.height, 10) || 175}
              onChange={(v) => setAnswers(prev => ({ ...prev, height: String(v) }))}
              isUnset={!answers.height}
            />
          ) : current.type === 'number' ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="number"
                  value={answers[current.id]}
                  onChange={e => setAnswers(prev => ({ ...prev, [current.id]: e.target.value }))}
                  placeholder={current.placeholder}
                  min={current.min}
                  max={current.max}
                  autoFocus
                  style={{
                    flex: 1, padding: '14px 16px', borderRadius: 14, fontSize: 18, fontWeight: 600,
                    background: c.bg, border: `2px solid ${c.border}`, color: c.text,
                    outline: 'none', fontFamily: 'inherit',
                  }}
                />
                <span style={{ color: c.muted, fontSize: 15, fontWeight: 500, minWidth: 36 }}>{current.unit}</span>
              </div>
              {answers[current.id] && (() => {
                const num = parseFloat(answers[current.id]);
                const outOfRange = !isNaN(num) && (num < current.min || num > current.max);
                return outOfRange ? (
                  <p style={{ color: c.error, fontSize: 13, marginTop: 8 }}>
                    Please enter a value between {current.min} and {current.max} {current.unit}
                  </p>
                ) : null;
              })()}
            </div>
          ) : current.type === 'coach_code' ? (
            <CoachCodeInput onCoachValidated={setValidatedCoach} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {OPTIONS[current.id].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setAnswers(prev => ({ ...prev, [current.id]: opt.value }))}
                  style={{
                    padding: '14px 16px', borderRadius: 14, cursor: 'pointer', textAlign: 'left',
                    background: answers[current.id] === opt.value ? `${c.gold}18` : c.bg,
                    border: `2px solid ${answers[current.id] === opt.value ? c.gold : c.border}`,
                    color: c.text, fontSize: 15, fontWeight: 600, fontFamily: 'inherit',
                    transition: 'all 0.15s',
                  }}
                >
                  {opt.label}
                  {opt.sub && <div style={{ fontSize: 12, color: c.muted, fontWeight: 400, marginTop: 3 }}>{opt.sub}</div>}
                </button>
              ))}
            </div>
          )}

          {current.id === 'injuries' && answers.injuries === 'yes' && (
            <div style={{ marginTop: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: c.text, marginBottom: 8 }}>Please describe</label>
              <textarea
                value={answers.injuryDetails}
                onChange={e => setAnswers(prev => ({ ...prev, injuryDetails: e.target.value }))}
                placeholder="e.g. Lower back pain, knee issues…"
                rows={3}
                style={{ width: '100%', padding: '12px 14px', background: c.bg, border: `2px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', resize: 'vertical' }}
              />
            </div>
          )}
        </div>

        {/* Nav */}
        <div style={{ display: 'flex', gap: 10 }}>
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)} style={{ padding: '14px 20px', background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, color: c.text, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              ← Back
            </button>
          )}
          <button
            onClick={advance}
            disabled={!canAdvance || submitting}
            style={{
              flex: 1, padding: '14px 20px', borderRadius: 14, fontSize: 15, fontWeight: 700,
              cursor: !canAdvance || submitting ? 'not-allowed' : 'pointer',
              background: !canAdvance ? c.border : `linear-gradient(135deg, ${c.gold}, ${c.goldDark})`,
              border: 'none', color: !canAdvance ? c.muted : '#000', fontFamily: 'inherit',
            }}
          >
            {submitting ? 'Setting up…' : step === STEPS.length - 1 ? "Let's go →" : 'Continue →'}
          </button>
        </div>

        {/* A way out for the wrong account, or someone else's turn (Codex's task 019, R2) */}
        {onLogout && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button
              type="button"
              onClick={onLogout}
              disabled={submitting}
              style={{ background: 'none', border: 'none', color: c.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', minHeight: 44, padding: '0 12px' }}
            >
              Log out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
